import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { coinsService } from '@/services/coinsService';

const STORAGE_KEY = 'psyforge:last_daily_checkin';

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

/**
 * Mints the daily-checkin Coin reward at most once per UTC day per user.
 * Server-side is also idempotent on (source='daily_checkin', ref_id=YYYY-MM-DD)
 * so this is safe even if the localStorage guard is bypassed.
 */
export function useDailyCheckin() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fired = useRef(false);

  useEffect(() => {
    if (!user?.id || fired.current) return;
    const today = todayUtc();
    const key = `${STORAGE_KEY}:${user.id}`;
    try {
      const last = localStorage.getItem(key);
      if (last === today) {
        fired.current = true;
        return;
      }
    } catch {
      /* ignore storage errors */
    }
    fired.current = true;
    coinsService
      .mint({
        source: 'daily_checkin',
        ref_id: today,
        action_key: 'daily_checkin',
      })
      .then((result) => {
        // Any definitive server answer (minted, already minted, or on
        // cooldown/cap) means we should not retry again today.
        try {
          localStorage.setItem(key, today);
        } catch {
          /* ignore */
        }
        if (result.ok && !result.already_minted) {
          queryClient.invalidateQueries({ queryKey: ['coin-wallet'] });
          queryClient.invalidateQueries({ queryKey: ['coin-transactions'] });
        }
      })
      .catch(() => {
        // Silent — daily check-in is best-effort. Allow retry next session.
        fired.current = false;
      });
  }, [user?.id, queryClient]);
}